import { resoudreElementsCadre } from '@taki/data-model/cadre-canvas';
import { useCadreSurface } from '@taki/ui/cadre-canvas';
import { catalogueDuSuivi, estAutomatique, jourDansFuseau } from '@taki/data-model';
import type { ContexteDeCondition } from '@taki/data-model';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, JSX } from 'react';
import {
  BROUILLON_ID,
  CanvasBoundary,
  CreationProvider,
  champsDuJeu,
  useCreationPort,
  PhotosDeFiches,
  ScreenLayout,
  ViewHost,
  projectRecord,
  usePendingAppAction,
  usePersonnesPort,
  usePref,
  useScreen,
} from '@taki/ui';
import type { ContexteDAction, CreationPort, LinkedDoor, ScreenDoor, ScreenQuery, ViewId, ViewType, ViewBinding } from '@taki/ui';
import type { FamilleRepository } from '@taki/sdk';
import type { AppDeclaree, FicheGenerique, ProprietesDeCanvas } from './app-declaree.js';
import { assemblerEcran } from './assemblage.js';
import type { EcranAssemble, EnvironnementDesEffets } from './assemblage.js';
import type { AvisDEffet } from './effets-socle.js';
import { IconeDeFiche } from './icone.js';
import { jeuxDesInstances, resoudreDeclaration } from './resolution.js';
import type { ResolutionDeDeclaration } from './resolution.js';
import { champsDeCreation } from './acces.js';
import { useAppRuntime } from './runtime.js';
import { AiHistoryPanel } from './AiHistoryPanel.js';
import { contenuDeCarteFichiers } from './CarteDesFichiers.js';
import { useGestesDeFiche } from './gestes-de-fiche.js';
import { ancreDeLaFicheOuverte, chatterActifSurLeJeu, useSuiviDuGabarit } from './suivi.js';
import type { AncreDuSuivi } from './suivi.js';
import { bindingsSousVerrous, etapesDeLaFiche } from './etapes.js';
import { useActionsDeVueDeFiche } from './actions-de-vue-de-fiche.js';
import { useActionsDeFicheDeclarees } from './actions-de-fiche-declarees.js';
import { ContexteDeVues, typeDeVueHtml, vuesDeLApp } from './vues-de-fiche.js';
import { donneesDeVueHtml } from './vues-de-fiche-donnees.js';
import type { ApercuDeVueHtml, ContexteDesVuesDeFiche } from './vues-de-fiche.js';
import { clefDeJeu } from './assemblage.js';
import { politiqueDeFichiers } from './fichiers.js';
import { latestLoad } from './latestLoad.js';
import { useFournisseurDePhotos } from './photos.js';

/** Ce que `sdk.vuesHtml.lireBrouillon` rend, ce que l'apercu en lit (`VueHtmlBrouillon`). */
interface ApercuLu {
  id: string;
  porte: string;
  viewId: string;
  label: string;
  html: string;
  champs: string[];
  liens?: string[];
}

/**
 * L'ECRAN GENERIQUE DU SOCLE : il INTERPRETE une declaration, il ne la
 * recompile pas.
 *
 * CE QU'IL REMPLACE. Chaque app avait son `<Id>Screen.tsx` : cinq cents lignes
 * dont quatre cent quatre-vingts etaient les memes d'une app a l'autre (charger
 * chaque jeu avec son type, compter le jeu complet de chaque porte, poser les
 * bindings, raboter les filtres et les stats a la preference du poste, tenir la
 * puce de sortie, passer vingt props au gabarit). Ce qui differait tenait dans
 * deux fichiers de donnees et un registre de fonctions. Ce composant est ce
 * qu'il restait de commun, ecrit une fois.
 *
 * CE QU'IL NE FAIT PAS, ET NE FERA JAMAIS. Il n'evalue aucune expression, ne lit
 * aucune condition, ne construit aucune chaine a partir d'un gabarit : c'est la
 * ligne rouge de `packages/ui/src/views/screens.ts`, et elle vaut ici aussi.
 * Tout comportement vient soit d'un MECANISME DU SOCLE que le fichier NOMME,
 * soit d'une FONCTION DE L'APP que le fichier NOMME.
 *
 * LA STRUCTURE EST CELLE QUE LA CAGE IMPOSE (`docs/GABARIT-ECRAN.md`) :
 * - UN seul `ScreenLayout` a la racine, et RIEN a cote (regle G6) : la CREATION
 *   est une FICHE VIDE en zone 5, tenue par le socle ; l'EDITION se fait EN
 *   PLACE sur la fiche, enregistrement automatique. Seule exception : la
 *   fenetre d'app qu'un geste `niveau: "fiche"` ouvre, frere du gabarit ;
 * - zone 5 pilotee par les VUES du socle : aucune liste, aucun tableau, aucun
 *   kanban dessine a la main ;
 * - les etats standard vont au `ViewHost` ;
 * - acces data UNIQUEMENT via `@taki/sdk`, jamais depuis une vue.
 *
 * Les compteurs du menu se calculent sur le jeu COMPLET de chaque porte, jamais
 * sur la porte courante (G15).
 */

/* ------------------------------------------------------------------ */
/* Le chargement : un appel par JEU, pas par porte                     */
/* ------------------------------------------------------------------ */

/**
 * LE MODE ARCHIVES voyage dans la requete et c'est l'ECRAN qui le traduit en
 * option de SDK. On relaie `'all'`, JAMAIS `'only'`, et c'est un choix : cet
 * ecran est en pagination CLIENTE, donc tous ses chiffres se calculent sur ce
 * qu'il a charge, compteurs du menu compris. Avec `'only'` le jeu actif
 * quitterait la memoire et les compteurs tomberaient a zero le temps du mode, ce
 * que la regle G15 interdit. Avec `'all'` le moteur fait le partage : les
 * archives a l'ecran, les actifs dans les compteurs et les stats.
 *
 * Le cadrage par `orgId` est pose par le SDK, jamais par l'ecran.
 */
const modeDe = (query: ScreenQuery) =>
  ({ archived: query.archivesMode ? 'all' : 'exclude' }) as const;

/** Le `type` d'une fiche, lu sans rien supposer de son jeu. */
const typeDeFiche = (fiche: FicheGenerique): string =>
  typeof fiche['type'] === 'string' ? fiche['type'] : '';

/**
 * CHAQUE JEU INTERROGE AVEC SON TYPE, et ce n'est pas une optimisation : les
 * rules ne peuvent lire le type d'un document que si la REQUETE le fixe. Un
 * membre dont l'acces a une famille est regle par type se verrait refuser la
 * liste entiere sans ce filtre (`docs/ACCES.md`). Le `filter` qui suit est la
 * seconde moitie de la meme prudence : une famille est PARTAGEE par toutes les
 * apps de l'org, et un document ecrit avant le champ `type` remonte sans lui.
 */
async function chargerUnJeu(
  depot: FamilleRepository,
  type: string | undefined,
  query: ScreenQuery,
): Promise<FicheGenerique[]> {
  const fiches = await depot.list({
    ...(type === undefined ? {} : { type }),
    ...modeDe(query),
  });
  return type === undefined ? fiches : fiches.filter((fiche) => typeDeFiche(fiche) === type);
}

/**
 * TOUS LES JEUX SE CHARGENT ENSEMBLE, A CHAQUE PASSAGE.
 *
 * POURQUOI TOUS, ALORS QU'UNE SEULE PORTE EST A L'ECRAN : les COMPTEURS du menu
 * portent sur le jeu COMPLET de chaque porte et restent GELES (regle G15) ; ne
 * charger que la porte active ferait tomber les autres a zero des qu'on arrive
 * directement sur l'une d'elles (lien `?s=...`).
 */
async function chargerLesJeux(
  assemble: EcranAssemble,
  query: ScreenQuery,
): Promise<Map<string, FicheGenerique[]>> {
  const charges = await Promise.all(
    assemble.jeux.map(
      async ({ clef, jeu, depot }) => [clef, await chargerUnJeu(depot, jeu.type, query)] as const,
    ),
  );
  return new Map(charges);
}

/**
 * Ce qu'une porte montre, une fois les jeux charges : le jeu qu'elle declare,
 * limite a sa TRANCHE. C'est ici, et seulement ici, que la tranche s'applique :
 * compteur, vues, recherche et stats ne voient donc que ses fiches.
 */
function fichesDePorte(
  assemble: EcranAssemble,
  porteId: string,
  parJeu: Map<string, FicheGenerique[]>,
  contexte: ContexteDeCondition,
): FicheGenerique[] {
  const clef = assemble.clefDeJeuDePorte(porteId);
  const fiches = clef === null ? [] : (parJeu.get(clef) ?? []);
  const tranche = assemble.tranche(porteId, contexte);
  return tranche === null ? fiches : fiches.filter(tranche);
}

/**
 * CE QUE DEVIENNENT LES JEUX CHARGES : les compteurs du menu et les fiches des
 * portes canvas. UNE seule fonction, appelee par le chargement de l'ecran ET par
 * le recompte silencieux (cf. `EcranDeclare`) : deux calculs ecrits a cote
 * finiraient par ne plus dire la meme chose.
 *
 * LE COMPTEUR DIT LA MEME VERITE QUE LA ZONE 5 HORS MODE ARCHIVES : les archives
 * en sont exclues, dans les DEUX modes.
 */
function compterLesPortes(
  assemble: EcranAssemble,
  portesCanvas: Readonly<Record<string, unknown>>,
  parJeu: Map<string, FicheGenerique[]>,
  contexte: ContexteDeCondition,
): { comptes: Record<string, number>; canvas: Record<string, FicheGenerique[]> } {
  const comptes: Record<string, number> = {};
  const canvas: Record<string, FicheGenerique[]> = {};
  for (const porte of assemble.portes) {
    const fiches = fichesDePorte(assemble, porte.id, parJeu, contexte);
    comptes[porte.id] = fiches.filter((fiche) => fiche['archivedAt'] == null).length;
    if (portesCanvas[porte.id] !== undefined) canvas[porte.id] = fiches;
  }
  return { comptes, canvas };
}

/** Les fiches ACTIVES d'une liste : la verite du compteur du menu. */
const fichesActives = (fiches: readonly FicheGenerique[]): FicheGenerique[] =>
  fiches.filter((fiche) => fiche['archivedAt'] == null);

/* ------------------------------------------------------------------ */
/* L'ecran                                                             */
/* ------------------------------------------------------------------ */

/** Ce que le chassis rend de statistiques dans l'en-tete (zone 2c) : trois. */
const STATS_MAX = 3;

/**
 * L'ANTI-REBOND du recompte silencieux, en ms : une rafale d'ecritures (une
 * surface qui enregistre champ par champ, un import) ne fait qu'UNE relecture.
 */
const DELAI_DU_RECOMPTE = 150;

/** Combien de temps l'avis d'un effet du socle reste sur son bouton, en ms. */
const DUREE_DE_L_AVIS = 5000;

/**
 * L'ECRAN DECLARE, ET SON PORT DE CREATION.
 *
 * L'HOTE FOURNIT LA CREATION (`CreationProvider`, autour du shell) mais ne
 * connait pas la declaration : il ne sait pas qu'un champ est un `calcul` ou un
 * `numero`. L'ecran, lui, la connait. Il relaie donc le MEME port, une fois
 * passe par `champsDeCreation` (le numero retire, les calculs faits), avant de
 * monter le moteur qui le lit. Ce n'est pas un second chemin de creation :
 * c'est celui de l'hote, et une fiche liee creee d'ici le prend aussi, avec
 * les champs de SON jeu.
 */
export function EcranDeclare({ app }: { app: AppDeclaree }): JSX.Element {
  const { instances } = useAppRuntime();

  /*
   * LA DECLARATION EFFECTIVE : celle de l'org quand elle existe et se lit,
   * sinon celle du depot. Une instance illisible ne fait pas tomber l'ecran ;
   * elle part au journal, et le panneau de reglages la montre en clair.
   */
  const resolution = useMemo(
    () => resoudreDeclaration(app, instances?.[app.id], jeuxDesInstances(instances)),
    [app, instances],
  );
  useEffect(() => {
    if (resolution.raison === undefined) return;
    console.warn(
      `[taki/app-runtime] App "${app.id}" : la declaration de l'org n'a pas pu etre lue, ` +
        `celle du dépôt est montée. ${resolution.raison}`,
    );
  }, [app.id, resolution.raison]);

  const creationDeLHote = useCreationPort();
  const champs = resolution.declaration.champs;
  const creation = useMemo<CreationPort | null>(
    () =>
      creationDeLHote === null
        ? null
        : {
            create: async (entree) =>
              await creationDeLHote.create({
                ...entree,
                fields: champsDeCreation(
                  champsDuJeu(champs, entree.family, entree.type === '' ? undefined : entree.type),
                  entree.fields,
                  entree.family,
                ),
              }),
          },
    [creationDeLHote, champs],
  );

  return (
    <CreationProvider value={creation}>
      <EcranResolu app={app} resolution={resolution} />
    </CreationProvider>
  );
}

function EcranResolu({ app, resolution }: { app: AppDeclaree; resolution: ResolutionDeDeclaration }): JSX.Element {
  const { sdk, orgId } = useAppRuntime();
  const [jourCourant, poserJour] = useState(() => jourDansFuseau(new Date(), sdk.timezone));
  useEffect(() => {
    const actualiser = () => poserJour(jourDansFuseau(new Date(), sdk.timezone));
    const timer = setInterval(actualiser, 30_000);
    document.addEventListener('visibilitychange', actualiser);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', actualiser); };
  }, [sdk.timezone]);

  /*
   * L'ASSEMBLAGE, fait UNE fois pour ce SDK et cette declaration : les bindings
   * recrees a chaque rendu relanceraient la projection a chaque frappe dans la
   * recherche.
   */
  /*
   * CE QUE LES EFFETS D'ACTION LISENT AU CLIC : la fiche ouverte, la selection,
   * la relecture de l'ecran. Des REFS, tenues a jour a chaque rendu plus bas :
   * l'assemblage est memoise, et le reconstruire a chaque ouverture de fiche
   * relancerait la projection. `environnement` est donc STABLE.
   *
   * L'AVIS D'UN EFFET DU SOCLE (« 3 fiches créées ») se rend SUR SON BOUTON, le
   * temps de le lire (`DUREE_DE_L_AVIS`), comme l'accuse du bouton « Copier » :
   * la rangee 3a n'a pas d'indicateur a elle, et une fenetre de plus a refermer
   * pour un geste reussi serait de trop.
   */
  const contexteRef = useRef<{ ficheId: string | null; selection: readonly string[] }>({
    ficheId: null,
    selection: [],
  });
  const rechargerRef = useRef<() => void>(() => {});
  /* LES JEUX CHARGES, tenus a jour AVANT que l'ecran projette ses lignes : les
     totaux lies par ligne les groupent (`totaux-lies.ts`). */
  const jeuxChargesRef = useRef<Map<string, FicheGenerique[]>>(new Map());
  const [avis, setAvis] = useState<{ porteId: string; effet: string; avis: AvisDEffet } | null>(
    null,
  );
  useEffect(() => {
    if (avis === null || avis.avis.etat === 'enCours') return;
    const minuteur = window.setTimeout(() => setAvis(null), DUREE_DE_L_AVIS);
    return () => window.clearTimeout(minuteur);
  }, [avis]);
  const environnement = useMemo<EnvironnementDesEffets>(
    () => ({
      lireContexte: (porteId: string): ContexteDAction => ({ porteId, ...contexteRef.current }),
      orgId: orgId ?? '',
      recharger: () => rechargerRef.current(),
      annoncer: (porteId, effet, avisRecu) => setAvis({ porteId, effet, avis: avisRecu }),
      lireJeu: (clef) => jeuxChargesRef.current.get(clef),
    }),
    [orgId],
  );

  /* LA PERSONNE CONNECTEE, pour les filtres « est moi » : lue au rendu, elle
     arrive apres le montage, et les filtres se recalculent avec elle. */
  const personnes = usePersonnesPort();
  const moiPersonId = personnes?.moi?.personId ?? null;
  const moiUid = personnes?.moi?.userId ?? null;
  const contexteDesFiltres = useMemo(
    () => ({ moi: { personId: moiPersonId, uid: moiUid } }),
    [moiPersonId, moiUid],
  );

  /* LES VUES DE L'APP (Canvas, HTML) en descripteurs, une fois par declaration :
     des descripteurs neufs a chaque rendu remonteraient la vue a chaque frappe. */
  const vuesEnPlus = useMemo(
    () => vuesDeLApp(resolution.declaration.ecran, app.canvas),
    [resolution.declaration.ecran, app.canvas],
  );
  const assemble = useMemo(
    () => assemblerEcran(resolution.declaration, sdk, app.fonctions, app.effets, environnement, vuesEnPlus),
    [resolution.declaration, sdk, app.fonctions, app.effets, environnement, jourCourant, vuesEnPlus],
  );

  /*
   * LES COMPTEURS DU MENU, memorises a chaque chargement : ils portent sur le
   * jeu COMPLET de chaque porte, jamais sur la porte courante (G15). Un seul
   * etat pour toutes les portes : un `useState` par porte dirait le meme fait
   * plusieurs fois, et finirait par ne plus dire la meme chose.
   */
  const [comptes, setComptes] = useState<Readonly<Record<string, number>>>({});
  const countLoad = useRef(latestLoad());
  /* LA DERNIERE REQUETE DU CHARGEUR, et ce que le recompte silencieux relit a
     chaque passage : il rejoue le meme mode (archives ou non) que l'ecran. */
  const derniereQuery = useRef<ScreenQuery | null>(null);

  /* LES FICHES DE LA PORTE CANVAS, gardees a part : sa zone 5 appartient a
     l'app (G23), et sa surface les dessine. Vide tant qu'aucune porte n'en
     declare, ce qui est le cas ordinaire. */
  const [fichesDeCanvas, setFichesDeCanvas] = useState<Readonly<Record<string, FicheGenerique[]>>>(
    {},
  );

  /*
   * CE QUE CHAQUE PORTE AJOUTE A SA DECLARATION.
   *
   * TOUT CE QUI EST UN MOT OU UN NOM VIENT DE `taki.screens.json` ; ce qui reste
   * ici est ce qu'aucun fichier de donnees ne peut porter : l'entree a froid et
   * les gestes de fiche (l'assemblage les a cables sur le SDK), et la preference
   * du POSTE.
   *
   * PAS DE `load` PAR PORTE, ET C'EST VOULU : le chargeur de l'ECRAN charge tous
   * les jeux et rend la tranche de la porte demandee. Une porte qui ne
   * chargerait qu'elle priverait les autres de leur compteur.
   */
  /*
   * L'APERCU D'UNE VUE HTML EN BROUILLON (`?apercu=<id>`, chantier
   * `fiche-suivi`) : le brouillon que `vue_proposer` a depose, lu par le SDK
   * (son auteur seul le peut), monte comme une vue de fiche de SA porte. Le
   * moteur porte la clef d'adresse ; l'ecran lit et publie.
   */
  const [brouillonDeVue, poserBrouillonDeVue] = useState<{
    id: string;
    porte: string;
    viewId: string;
    label: string;
    html: string;
    champs: readonly string[];
    liens?: readonly string[];
  } | null>(null);
  const [vuePubliee, poserVuePubliee] = useState<string | null>(null);
  const typeDApercu = useMemo<ViewType | null>(
    () => (brouillonDeVue === null ? null : typeDeVueHtml({ ...brouillonDeVue, id: brouillonDeVue.viewId })),
    [brouillonDeVue],
  );
  const types = useMemo(
    () => (typeDApercu === null ? assemble.types : [...assemble.types.filter((type) => type.id !== typeDApercu.id), typeDApercu]),
    [assemble.types, typeDApercu],
  );

  /* LE KANBAN RESPECTE LES VERROUS de l'en-tete de sa porte (`bindingsSousVerrous`). Le tableau rendu
     est memorise par tableau d'entree (le moteur ne relance pas sa projection pour rien) et se
     refait avec le contexte (la personne connectee) que lisent les conditions. */
  const sousVerrous = useMemo(() => {
    const memo = new WeakMap<object, ViewBinding<FicheGenerique>[]>();
    return (porteId: string, bindings: ViewBinding<FicheGenerique>[]): ViewBinding<FicheGenerique>[] => {
      const connu = memo.get(bindings);
      if (connu !== undefined) return connu;
      const evaluer = assemble.evaluer(porteId, contexteDesFiltres);
      const sous = bindingsSousVerrous(bindings, {
        etapes: assemble.ecran.portes.find((porte) => porte.id === porteId)?.etapes,
        champsDuJeu: assemble.champsDePorte(porteId),
        evaluer: (condition, fiche) => evaluer(condition, fiche),
      });
      memo.set(bindings, sous);
      return sous;
    };
  }, [assemble, contexteDesFiltres]);

  const doors: ScreenDoor<FicheGenerique>[] = assemble.portes.map((porte) => {
    const depot = assemble.depotDePorte(porte.id);
    const jeu = assemble.jeuDePorte(porte.id);
    const duJeu = (fiche: FicheGenerique): boolean => jeu?.type === undefined || typeDeFiche(fiche) === jeu.type;
    /* LA FICHE OUVERTE SUIT LE SERVEUR SUR UN JEU A CHAMP AUTOMATIQUE, et la
       seulement : c'est le seul cas ou le serveur reecrit une fiche que l'ecran
       vient d'ecrire (le numero attribue apres « Creer », le calcul refait
       apres une ecriture d'ailleurs, `docs/socle/champs-types.md`). Voir le
       travail d'un collegue en direct sur toute fiche est un autre chantier. */
    const suivie = assemble.champsDePorte(porte.id).some((champ) => estAutomatique(champ.type));
    const declarees = assemble.stats(porte.id);
    const estCanvas = app.canvas[porte.id] !== undefined;
    /* LA PORTE DU BROUILLON MONTRE SON APERCU parmi ses vues de fiche. */
    const avecApercu = typeDApercu !== null && brouillonDeVue?.porte === porte.id;
    return {
      ...porte,
      ...(avecApercu
        ? { viewIds: [...(porte.viewIds ?? assemble.types.map((type) => type.id)).filter((id) => id !== typeDApercu.id), typeDApercu.id] }
        : {}),
      ...(depot === null
        ? {}
        : {
            /* L'ENTREE A FROID LIT DANS LE BON JEU : un id venu d'ailleurs (le
               clic sur une ligne de carte liee) appartient au jeu de la porte
               LIEE, jamais a celui de l'ecran. */
            get: async (id: string) => {
              const fiche = await depot.get(id);
              if (fiche === null) return null;
              return duJeu(fiche) ? fiche : null;
            },
            /* Meme filtre de jeu que `get` : une fiche d'un autre type n'est
               pas celle de cette porte. */
            ...(suivie
              ? {
                  observe: (id, onChange, onError) =>
                    depot.observe(id, (fiche, meta) => onChange(fiche !== null && duJeu(fiche) ? fiche : null, meta), onError),
                }
              : {}),
            ...assemble.gestes(porte.id),
          }),
      /* L'apercu lit le binding de la Fiche, par la regle de l'assemblage. */
      bindings: sousVerrous(porte.id, assemble.bindings(porte.id, avecApercu ? [typeDApercu] : [])),
      filters: assemble.filtres(porte.id, contexteDesFiltres),
      presets: assemble.favoris(porte.id),
      /* Les agregats declares, calcules sur le jeu VIVANT : les trois premiers
         dans l'ordre de la declaration (le Studio choisit et ordonne).
         SUR UNE PORTE CANVAS, le jeu vivant est celui du dernier chargement, que
         ses ecritures directes ne rechargent pas : les stats se calculent donc
         sur les fiches de la porte que le recompte silencieux tient a jour,
         archives exclues (la meme verite que le compteur du menu). */
      stats: (live: FicheGenerique[]) =>
        declarees(estCanvas ? fichesActives(fichesDeCanvas[porte.id] ?? []) : live).slice(0, STATS_MAX),
      actions: assemble.actions(porte.id).map((action) => {
        /* L'AVIS PREND LA PLACE DU LIBELLE, sur le seul bouton qui l'a
           declenche : meme porte, meme effet. En cours ou en echec, le bouton
           est eteint, et son survol dit la phrase entiere (G16). */
        const fn = assemble.ecran.portes
          .find((declaree) => declaree.id === porte.id)
          ?.actions?.find((declaree) => declaree.id === action.id)?.fn;
        if (avis === null || avis.porteId !== porte.id || avis.effet !== fn) return action;
        const eteint = avis.avis.etat !== 'fait';
        const raison = avis.avis.detail ?? avis.avis.texte;
        return {
          ...action,
          label: avis.avis.texte,
          ...(eteint ? { disabled: true, disabledReason: raison } : {}),
        };
      }),
    };
  });

  const screen = useScreen<FicheGenerique>({
    appId: app.id,
    /* LE FUSEAU DE L'ORG dit quel jour il est pour `socle:aujourdhui`. */
    fuseau: sdk.timezone,
    screenId: assemble.ecran.id,
    /* LE CHARGEUR EST PARTAGE PAR TOUTES LES PORTES : elles se distinguent par
       `query.section`, et c'est le moteur qui le rappelle au changement de
       porte. Il charge chaque jeu une fois, memorise les compteurs, puis rend
       la tranche demandee. */
    load: async (query) => {
      derniereQuery.current = query;
      const commitIfLatest = countLoad.current();
      const parJeu = await chargerLesJeux(assemble, query);
      jeuxChargesRef.current = parJeu;
      const { comptes: frais, canvas } = compterLesPortes(assemble, app.canvas, parJeu, contexteDesFiltres);
      commitIfLatest(() => {
        setComptes(frais);
        setFichesDeCanvas(canvas);
      });
      return fichesDePorte(assemble, query.section ?? assemble.porteArrivee, parJeu, contexteDesFiltres);
    },
    getId: (record) => String(record['id'] ?? ''),
    types,
    bindings: sousVerrous(assemble.porteArrivee, assemble.bindings(assemble.porteArrivee)),
    doors,
    // La vue et la porte d'arrivee sont DECLAREES : le schema du fichier refuse
    // une vue d'arrivee absente du catalogue monte.
    defaultView: assemble.vueArrivee,
    /* LA PILE D'ARRIVEE VIENT DU FICHIER, comme le catalogue de tri. Vide tant
       que rien ne la declare : le jeu arrive dans l'ordre du chargeur. */
    defaultSort: assemble.tri.defaut,
    defaultSection: assemble.porteArrivee,
  });
  /* LES LECTURES DES EFFETS, a jour a chaque rendu. Un brouillon, un modele ou
     un enfant de modele ne sont pas des fiches : l'effet ne recoit alors aucune
     fiche, plutot qu'un id qu'aucun repository ne connait. */
  const ficheOuverte =
    screen.openId == null ||
    screen.openId === BROUILLON_ID ||
    screen.openId.includes('/') ||
    screen.modelesMode
      ? null
      : screen.openId;
  contexteRef.current = { ficheId: ficheOuverte, selection: screen.selection };

  /*
   * LE RECOMPTE SILENCIEUX D'UNE PORTE CANVAS.
   *
   * POURQUOI. Sur une porte NATIVE, chaque geste (creation, archivage,
   * restauration, suppression) rappelle `reload()` : le chargeur repasse et les
   * compteurs, les stats et les fiches suivent. Sur une porte CANVAS (G23), la
   * surface de l'app ecrit DIRECTEMENT par le SDK : personne ne relance le
   * chargeur, et la pastille du menu comme les stats de l'en-tete resteraient
   * figees jusqu'au rechargement de la page. Le SDK dit chaque ecriture reussie
   * (`sdk.ecritures`) ; l'ecran n'a plus qu'a recompter.
   *
   * SEULEMENT SUR UNE PORTE CANVAS : ailleurs les gestes rechargent deja, un
   * recompte de plus serait une double lecture. Et JAMAIS PAR `screen.reload()` :
   * il allumerait l'etat de chargement du gabarit et demonterait la surface, qui
   * perdrait son etat en pleine saisie. On relit les jeux a cote, et on ne
   * touche que ce qui est compte (menu, stats, fiches de la surface).
   *
   * PAS DE MENSONGE SUR UN ECHEC : l'ecriture a reussi, on le dit en console et
   * c'est le prochain chargement qui fera foi. Le jeton `latestLoad` laisse le
   * dernier arrive gagner : un chargement ou un recompte plus recent, lance
   * pendant la lecture, rend celle-ci caduque.
   */
  const assembleRef = useRef(assemble);
  assembleRef.current = assemble;
  const contexteDesFiltresRef = useRef(contexteDesFiltres);
  contexteDesFiltresRef.current = contexteDesFiltres;
  const porteActiveRef = useRef(screen.door.id);
  porteActiveRef.current = screen.door.id;
  useEffect(() => {
    /* Un faux SDK de test ou un SDK plus ancien peut n'avoir pas de signal : */
    if (sdk.ecritures === undefined) return;
    const familles = new Set(assemble.jeux.map(({ jeu }) => jeu.family));
    let minuteur: number | undefined;
    let vivant = true;
    const recompter = async () => {
      const query = derniereQuery.current;
      if (query === null) return;
      const commitIfLatest = countLoad.current();
      try {
        const parJeu = await chargerLesJeux(assembleRef.current, query);
        const { comptes: frais, canvas } = compterLesPortes(
          assembleRef.current,
          app.canvas,
          parJeu,
          contexteDesFiltresRef.current,
        );
        commitIfLatest(() => {
          if (!vivant) return;
          jeuxChargesRef.current = parJeu;
          setComptes(frais);
          setFichesDeCanvas(canvas);
        });
      } catch (erreur) {
        console.warn('[taki/app-runtime] Le recompte apres ecriture a echoue, le prochain chargement fera foi.', erreur);
      }
    };
    const arreter = sdk.ecritures.observer((ecriture) => {
      if (!familles.has(ecriture.famille)) return;
      if (app.canvas[porteActiveRef.current] === undefined) return;
      /* Un recompte deja en vol lit des donnees d'AVANT cette ecriture : on le
         rend caduc tout de suite, le suivant partira apres l'anti-rebond. */
      countLoad.current();
      window.clearTimeout(minuteur);
      minuteur = window.setTimeout(() => {
        minuteur = undefined;
        void recompter();
      }, DELAI_DU_RECOMPTE);
    });
    return () => {
      vivant = false;
      arreter();
      window.clearTimeout(minuteur);
    };
  }, [sdk, assemble, app.canvas]);

  /* LA CAPACITE FICHIERS DU JEU DE LA PORTE, sur la declaration EFFECTIVE. */
  const jeuAffiche = assemble.jeuDePorte(screen.door.id);
  const politique = politiqueDeFichiers(resolution.declaration.champs, jeuAffiche);
  /*
   * LE CONTENU DE LA CARTE DES FICHIERS, pose par la fiche a son rang. `null` :
   * le jeu ne les ouvre pas, la carte n'apparait pas, et pas davantage sur un
   * modele ou un enfant de modele (aucun fichier ne s'y joint). Sur un
   * BROUILLON, pas encore en base, la fiche rend son apercu a la place de ce
   * contenu : il suffit de ne pas dire `null`.
   */
  const fichiers = contenuDeCarteFichiers({
    sdk, jeu: jeuAffiche, politique, ficheOuverte,
    brouillon: screen.openId === BROUILLON_ID && !screen.modelesMode,
  });
  /* LES PHOTOS DE REFERENCE : un fournisseur par ecran (cache par id, URL d'objet
     revoquees au demontage), pose autour du ViewHost. Sans lui, pas de photo. */
  const photos = useFournisseurDePhotos(sdk);
  rechargerRef.current = screen.reload;
  /* UNE TRANCHE « MOI » SE RELIT QUAND LA PERSONNE CONNECTEE ARRIVE : le
     premier chargement part souvent avant elle, et la porte resterait vide. */
  const trancheeSurMoi = assemble.portes.some((porte) => assemble.tranche(porte.id) !== null);
  const moiCharge = useRef(contexteDesFiltres);
  useEffect(() => {
    if (moiCharge.current === contexteDesFiltres) return;
    moiCharge.current = contexteDesFiltres;
    if (trancheeSurMoi) rechargerRef.current();
  }, [contexteDesFiltres, trancheeSurMoi]);

  /* LES GESTES DE FICHE DECLARES (`EFFETS_DE_FICHE`) : dans le menu « ... » de
     la fiche ouverte, a la suite de ceux du moteur. */
  const gestesDeFiche = useGestesDeFiche({
    sdk,
    actions: assemble.ecran.portes.find((porte) => porte.id === screen.door.id)?.actions,
    porte: assemble.ecran.portes.find((porte) => porte.id === screen.door.id)?.label ?? '',
    jeu: jeuAffiche,
    politique,
    ficheOuverte,
    record: ficheOuverte === null ? undefined : (screen.openRecordValue ?? undefined),
    binding: assemble.bindings(screen.door.id).find((binding) => binding.type === 'core.fiche'),
    raisonAcces: screen.raisonAcces,
  });

  /*
   * LE SUIVI DE LA FICHE OUVERTE (prop `suivi` du gabarit, `suivi.ts`) : le
   * rail et le volet. Aucun sur un brouillon, un modele ou un enfant de modele :
   * pas de fiche en base a suivre. Le titre voyage avec l'ancre pour que le
   * centre nomme la fiche sans la relire (« Ici : Studio Lumiere SARL »).
   */
  const ficheAffichee = ficheOuverte === null ? undefined : (screen.openRecordValue ?? undefined);
  /* LE TITRE TEL QUE LA FICHE L'AFFICHE (`titreAffichableDeLaFiche`) : un titre
     a catalogue voyage par son LIBELLE (« Rédaction »), un titre-REFERENCE par le
     NOM de la fiche designee (annuaires de la porte), jamais par une clef ni un
     id, vers le centre de messages et les notifications du suivi. Sans nom
     resolu : vide, et le suivi dit « Suivi de la fiche » tout court. */
  const bindingDeFiche = assemble.bindings(screen.door.id).find((binding) => binding.type === 'core.fiche');
  /* PAS D'ANCRE TANT QUE LA FICHE N'EST PAS LUE (`ancreDeLaFicheOuverte`) : juste
     apres « Creer », l'id est dans l'adresse avant la valeur, et un rail sans le
     nom de la fiche aurait dit « Suivi de la fiche » le temps de la lecture. */
  const ancreDuSuivi: AncreDuSuivi | null = ancreDeLaFicheOuverte({
    ficheOuverte,
    record: ficheAffichee,
    surfaceSansLecture: app.canvas[screen.door.id] !== undefined,
    jeu: jeuAffiche,
    // La FICHE seulement (R9 du 06/10/2026), et seulement si sa base ne l'a pas
    // eteint (`jeux[].chatter: false`, la clef garde son nom de donnee).
    suiviActif: jeuAffiche !== null && chatterActifSurLeJeu(resolution.declaration.champs, jeuAffiche),
    appId: app.id,
    doorId: screen.door.id,
    binding: bindingDeFiche,
    annuaires: screen.annuaires,
  });
  /* LE CATALOGUE DE LA DECLARATION pour le jeu de la fiche (`catalogueDuSuivi`,
     la seule regle des mots, celle du serveur) : un ANCIEN changement, ecrit
     avec des clefs, se remet en mots ; un neuf l'est deja. */
  const familleAffichee = jeuAffiche?.family ?? null;
  const typeAffiche = jeuAffiche?.type ?? null;
  const catalogueDuSuiviDeLaFiche = useMemo(
    () => (familleAffichee === null ? new Map() : catalogueDuSuivi(
      [{ champs: resolution.declaration.champs, ecrans: { ecrans: [resolution.declaration.ecran] } }],
      { family: familleAffichee, type: typeAffiche },
    )),
    [familleAffichee, typeAffiche, resolution.declaration.champs, resolution.declaration.ecran],
  );
  /* UNE PORTE CANVAS N'A PAS DE FICHE DE GABARIT, meme une fiche ouverte (sa
     surface la recoit, `ficheOuverte`) : le suivi y est le bouton « Suivi »,
     qui ouvre le centre, sans rail ni volet. Ecouter le fil en temps reel pour
     un bouton serait une lecture continue que personne ne lit. */
  const Canvas = app.canvas[screen.door.id] as ComponentType<ProprietesDeCanvas> | undefined;
  const suivi = useSuiviDuGabarit(sdk, ancreDuSuivi, {
    fil: Canvas === undefined,
    catalogue: catalogueDuSuiviDeLaFiche,
    lectureSeule: screen.raisonAcces != null,
  });

  /*
   * LES EN-TETES DE LA RANGEE 3 (`porte.etapes`, `etapes.ts`) : une piste par
   * en-tete de la porte, dans son ordre, chacune ecrite par le chemin de
   * l'edition en place. Rien sur un brouillon (il n'est pas en base), un
   * modele, un enfant de modele.
   */
  const porteDeclareeActive = assemble.ecran.portes.find((porte) => porte.id === screen.door.id);
  const etapes = etapesDeLaFiche({
    etapes: porteDeclareeActive?.etapes,
    binding: bindingDeFiche,
    champsDuJeu: assemble.champsDePorte(screen.door.id),
    fiche: ficheAffichee ?? null,
    ficheId: ficheOuverte,
    raisonAcces: screen.raisonAcces,
    patchColumn: screen.patchColumn,
    patchRecord: screen.patchRecord,
    valeurEnCours: screen.valeurEnCours,
    evaluer: (condition, surcharge) =>
      ficheAffichee !== undefined && ficheAffichee !== null
        ? assemble.evaluer(screen.door.id, contexteDesFiltres)(condition, ficheAffichee, surcharge)
        : false,
  });

  /* L'APERCU : le brouillon se lit quand l'adresse le demande, et la vue s'ouvre
     dessus une fois lue. Un SDK sans `vuesHtml` (branche plus ancienne), un
     brouillon expire ou celui d'un autre : pas d'apercu, la fiche reste. */
  const apercuDemande = screen.apercu;
  useEffect(() => {
    poserVuePubliee(null);
    if (apercuDemande === null) { poserBrouillonDeVue(null); return; }
    const vuesHtml = (sdk as unknown as { vuesHtml?: { lireBrouillon(id: string): Promise<ApercuLu | null> } }).vuesHtml;
    if (vuesHtml === undefined) return;
    let vivant = true;
    void vuesHtml.lireBrouillon(apercuDemande).then(
      (lu) => {
        if (!vivant) return;
        poserBrouillonDeVue(lu === null ? null : {
          id: lu.id, porte: lu.porte, viewId: lu.viewId, label: lu.label, html: lu.html,
          champs: lu.champs, ...(lu.liens === undefined ? {} : { liens: lu.liens }),
        });
      },
      (erreur: unknown) => console.warn("[taki/app-runtime] L'apercu de vue n'a pas pu etre lu.", erreur),
    );
    return () => { vivant = false; };
  }, [sdk, apercuDemande]);
  const { setView: montrerLaVue, viewId: vueActive, fermerApercu } = screen;
  /* LES ACTIONS QUE LA VUE DE FICHE ACTIVE POSE (`poserActions`, `actions-de-vue-de-fiche.ts`) :
     la rangee 3 et le menu « ... » de la fiche les rendent, ils ne valent que pour
     cette vue et cette fiche, jamais sur un brouillon (`ficheOuverte` est nulle). */
  const { poser: poserActionsDeVue, rangee: gestesDeVue, menu: menuDeVue } = useActionsDeVueDeFiche(vueActive, ficheOuverte);
  /* LES GESTES DE NIVEAU FICHE QUE LA DECLARATION POSE (`niveau: "fiche"`,
     `actions-de-fiche-declarees.tsx`) : meme rangee, APRES ceux que la vue pose,
     sur toutes les vues de la fiche. Leur fenetre d'app se monte a cote du
     gabarit. */
  const actionsDeFiche = useMemo(() => assemble.actionsDeFiche(screen.door.id), [assemble, screen.door.id]);
  const { rangee: gestesDeclares, fenetre: fenetreDeFiche } = useActionsDeFicheDeclarees({
    actions: actionsDeFiche,
    fenetres: app.fenetres,
    porte: screen.door.id,
    ficheOuverte,
    fiche: ficheAffichee,
    raisonAcces: screen.raisonAcces,
  });
  const ouvertSurLApercu = useRef<string | null>(null);
  useEffect(() => {
    if (brouillonDeVue === null || ficheOuverte === null || ouvertSurLApercu.current === brouillonDeVue.id) return;
    if (!screen.views.some((vue) => vue.id === brouillonDeVue.viewId)) return;
    ouvertSurLApercu.current = brouillonDeVue.id;
    if (vueActive !== brouillonDeVue.viewId) montrerLaVue(brouillonDeVue.viewId as ViewId);
  }, [brouillonDeVue, ficheOuverte, screen.views, vueActive, montrerLaVue]);
  /* PUBLIEE, la vue rejoint la declaration de l'org : l'apercu se referme quand
     elle y est, pas avant (sinon la fiche perdrait sa vue le temps d'un aller-retour). */
  useEffect(() => {
    if (vuePubliee === null) return;
    if ((resolution.declaration.ecran.vuesHtml ?? []).some((vue) => vue.id === vuePubliee)) fermerApercu();
  }, [vuePubliee, resolution.declaration.ecran.vuesHtml, fermerApercu]);
  const publierLApercu = useCallback(async (): Promise<void> => {
    if (brouillonDeVue === null) return;
    const vuesHtml = (sdk as unknown as { vuesHtml?: { publier(id: string): Promise<unknown> } }).vuesHtml;
    if (vuesHtml === undefined) throw new Error("le socle ne publie pas les vues (sdk.vuesHtml absent)");
    await vuesHtml.publier(brouillonDeVue.id);
    poserVuePubliee(brouillonDeVue.viewId);
  }, [sdk, brouillonDeVue]);

  /*
   * CE QUE LES VUES DE L'APP LISENT (`vues-de-fiche.tsx`) : la porte, ses
   * fiches brutes, la fiche ouverte, les donnees d'une vue HTML et l'apercu.
   */
  const jeuxCharges = jeuxChargesRef.current;
  const contexteDesVues = useMemo<ContexteDesVuesDeFiche>(() => {
    const porteAssemblee = assemble.portes.find((porte) => porte.id === screen.door.id);
    const options = new Map<string, ReadonlyMap<string, string>>();
    for (const colonne of bindingDeFiche?.fieldColumns ?? bindingDeFiche?.columns ?? []) {
      const liste = colonne.options?.();
      if (liste !== undefined && liste.length > 0) {
        options.set(colonne.path ?? colonne.key, new Map(liste.map((option) => [option.value, option.label])));
      }
    }
    const lireJeu = (lien: LinkedDoor) =>
      jeuxCharges.get(clefDeJeu({ family: lien.family, ...(lien.type === undefined ? {} : { type: lien.type }) }));
    const titreDe = (lien: LinkedDoor, liee: FicheGenerique): string => {
      const binding = assemble.bindings(lien.door).find((candidate) => candidate.type === 'core.liste')
        ?? assemble.bindings(lien.door)[0];
      const id = String(liee['id'] ?? '');
      return binding === undefined ? id : projectRecord(liee, id, binding).title;
    };
    const apercu: ApercuDeVueHtml | null = brouillonDeVue === null
      ? null
      : {
          viewId: brouillonDeVue.viewId,
          label: brouillonDeVue.label,
          html: brouillonDeVue.html,
          champs: brouillonDeVue.champs,
          ...(brouillonDeVue.liens === undefined ? {} : { liens: brouillonDeVue.liens }),
          publier: publierLApercu,
          ...(vuePubliee === null ? {} : { raison: 'Publiée' }),
        };
    return {
      porte: screen.door.id,
      fiches: screen.records,
      fiche: ficheAffichee ?? null,
      donneesDe: (vue) =>
        ficheAffichee === undefined || jeuAffiche === null
          ? null
          : donneesDeVueHtml({
              fiche: ficheAffichee,
              jeu: jeuAffiche,
              vue,
              champsDuJeu: assemble.champsDePorte(screen.door.id),
              options,
              liens: porteAssemblee?.links ?? [],
              lireJeu,
              titreDe,
            }),
      apercu,
      poserActions: poserActionsDeVue,
    };
  }, [assemble, screen.door.id, screen.records, ficheAffichee, jeuAffiche, bindingDeFiche, jeuxCharges, brouillonDeVue, publierLApercu, vuePubliee, poserActionsDeVue]);

  /*
   * LE RACCOURCI DU DOCK ECRIT DANS LA FICHE (`fiche.actions`), le seul qui
   * passe ici : il vise l'app sans nommer de porte, et c'est ICI qu'il ouvre la
   * MEME fiche vide que le « + Ajouter » de la porte. Les raccourcis DERIVES
   * des portes (`raccourcis.ts` de `@taki/shell`) nomment leur porte : le shell
   * y passe par l'adresse et c'est le GABARIT qui les consomme, pas cet effet.
   *
   * LA PORTE EST CELLE QUI DECLARE L'AJOUT, et elle doit l'etre : le dock peut
   * lancer l'app depuis n'importe ou, donc la porte active a l'arrivee n'est pas
   * forcement celle qui sait creer. Le moteur y passe d'abord, puis ouvre le
   * brouillon.
   *
   * IL NE VOIT PAS L'INTENTION DU SOCLE : le « + Ajouter » d'une carte liee pose
   * une intention qui nomme une PORTE et n'a pas d'`appId`, donc
   * `usePendingAppAction` la laisse passer et c'est le GABARIT qui la consomme.
   */
  const { actionId: intention, consume: consommerLIntention } = usePendingAppAction(app.id);
  const { ouvrirBrouillon } = screen;
  useEffect(() => {
    if (intention == null) return;
    const porte = assemble.ecran.portes.find((p) =>
      (p.actions ?? []).some((action) => action.id === intention && action.kind === 'add'),
    );
    if (porte !== undefined) ouvrirBrouillon({ porte: porte.id });
    consommerLIntention();
  }, [intention, consommerLIntention, ouvrirBrouillon, assemble]);

  /*
   * LE MENU : les LIBELLES viennent de la declaration (renommer une porte est
   * une ecriture de chaine dans `taki.screens.json`), les COMPTEURS sont ceux du
   * dernier chargement, sur le jeu COMPLET de chaque porte (G15).
   */
  const menuItems = useMemo(
    () =>
      /* UNE PORTE HORS NAVIGATION (`navigation: false`) n'a pas d'entree : on y
         entre par un lien d'une autre porte. */
      assemble.portes
        .filter((porte) => porte.navigation !== false)
        .map((porte) => ({
          id: porte.id,
          label: porte.label,
          count: comptes[porte.id] ?? 0,
        })),
    [assemble, comptes],
  );

  /*
   * Le libelle de la puce de sortie de la rangee 3 : le nom de la vue collection
   * ou la sortie de fiche ramene. L'ecran ne teste AUCUN niveau, c'est le
   * gabarit qui le deduit de `views` + `activeViewId`.
   */
  const exitLabel = screen.views.find((vue) => vue.id === screen.lastCollectionViewId)?.label;

  /*
   * LES ETATS VIDE ET INTROUVABLE VIENNENT DE LA PORTE, et rien n'est ecrit ici
   * quand elle n'en declare pas : le `ViewHost` porte deja les phrases neutres
   * du socle, et les recopier en ferait deux mots pour un etat.
   */
  const porteDeclaree = assemble.ecran.portes.find((porte) => porte.id === screen.door.id);
  const vide = porteDeclaree?.vide;
  const introuvable = porteDeclaree?.introuvable;

  /* LA FICHE QUE L'ADRESSE OUVRE SUR UNE PORTE CANVAS (`auClic: { porte }`
     d'une autre porte, un lien recopie) : sa surface la recoit, le moteur
     garde l'`id` dans l'adresse (`idDansLAdresse`). */
  const ficheDuCanvas = Canvas !== undefined && screen.openId !== null && screen.openId !== BROUILLON_ID && jeuAffiche !== null
    ? { family: jeuAffiche.family, id: screen.openId }
    : null;
  const { openRecord: fermerOuOuvrir, openLinked, creerDepuis } = screen;
  const fermerFicheDuCanvas = useCallback(() => fermerOuOuvrir(null), [fermerOuOuvrir]);
  const libelleDeLaPorte = screen.door.label;
  const ouvrirFicheDepuisLeCanvas = useCallback(
    (porte: string, id: string) => { openLinked(porte, id, { origine: libelleDeLaPorte }); },
    [openLinked, libelleDeLaPorte],
  );
  const creerDepuisLeCanvas = useCallback(
    (porte: string) => creerDepuis(porte, libelleDeLaPorte),
    [creerDepuis, libelleDeLaPorte],
  );
  const cadreSurface = useCadreSurface(screen.door.id, screen.door.canvas ?? { level: 'space' }, screen.query.search);
  const { tools: outilsSurface, ...propsSurface } = cadreSurface.props;

  return (
    <>
    <ScreenLayout
      /* HORS GABARIT (G23) : quand la porte active est un Space Canvas, le
         moteur le sert deja resolu et l'ecran le passe TEL QUEL. Sur une porte
         ordinaire il vaut `null`, et le gabarit ne change rien. */
      canvas={screen.door.canvas}
      /* LES ETATS STANDARD DE LA ZONE 5, mais SEULEMENT sur une porte canvas :
         ailleurs c'est le `ViewHost` qui les tient, et les passer deux fois est
         interdit (checklist, item 10). Sur un canvas il n'y a pas de ViewHost :
         sans ces props, un chargement rate s'afficherait comme une surface
         vide, sans moyen de relancer. */
      {...(screen.door.canvas != null
        ? {
            loading: screen.status.loading,
            ...(screen.status.error != null
              ? { error: screen.status.error.message, onRetry: screen.reload }
              : {}),
            errorTitle: 'Échec du chargement',
            retryLabel: 'Réessayer',
          }
        : {})}
      /* Zone 1 : identite de l'app (1a) et menu des portes (1b), avec une
         pastille par entree, calculee sur le jeu COMPLET de sa porte. */
      appName={resolution.declaration.fiche.name}
      appIcon={<IconeDeFiche nom={resolution.declaration.fiche.icon} size={22} />}
      menuItems={screen.door.canvas !== null && !resoudreElementsCadre(screen.door.canvas).includes('portes') ? [] : menuItems}
      activeMenuId={screen.chemin?.porteDuMenu ?? screen.section ?? assemble.porteArrivee}
      onMenuSelect={screen.setSection}
      /* Zone 2a : le h1 affiche le label de la porte active ; `title` est le
         repli. Zone 2b : la recherche, avec le placeholder de la porte. Zone
         2c : les stats de la porte, resolues par le moteur ; la porte les a deja
         rabotees au reglage du poste. */
      title={resolution.declaration.fiche.name}
      searchValue={screen.query.search}
      onSearchChange={screen.setSearch}
      searchPlaceholder={screen.door.searchPlaceholder}
      headerStats={screen.door.stats}
      /* Zone 3a : les actions de la porte, puis celles que la vue active
         propose. Zones 3b + 4 : outils, tags et favoris ; le catalogue de tri
         est DECLARE par l'ecran et vaut pour toutes ses portes. */
      quickActions={screen.door.actions}
      viewActions={screen.viewActions}
      tools={Canvas !== undefined ? { ...screen, ...outilsSurface } : screen}
      {...(gestesDeFiche.length === 0 && menuDeVue.length === 0
        ? {}
        : { recordMenuActions: [...menuDeVue, ...screen.recordMenuActions, ...gestesDeFiche] })}
      /* LES GESTES VISIBLES DE LA FICHE (rangee 3) : ceux que la vue de fiche
         active pose. Absents, la prop n'est pas passee : rien ne change. */
      {...(gestesDeVue.length === 0 && gestesDeclares.length === 0 ? {} : { recordActions: [...gestesDeVue, ...gestesDeclares] })}
      /* Le suivi de la fiche ouverte (rail et volet), rendu par le gabarit, lu
         par le socle (`suivi.ts`) ; et le champ a valeurs de la rangee 3. */
      suivi={suivi}
      etapes={etapes}
      sortOptions={assemble.tri.options}
      /* Zone 3c : le selecteur de vues, epingle, JAMAIS conditionnel. Une vue
         sans ce qu'elle exige y est GRISEE avec sa raison, jamais absente. */
      views={screen.views}
      activeViewId={screen.viewId}
      onViewSelect={screen.setView}
      /* Rangee 3 au niveau FICHE : la puce de sortie et l'indicateur
         d'enregistrement avec sa relance, passes SANS condition : le gabarit
         ne les rend qu'au niveau `record`, et une fiche sans eux serait une
         nasse. Le curseur « 1 / 5 » est parti (atelier du 26/10) ; Alt +
         fleche reste cable par le moteur. */
      onRecordExit={() => screen.openRecord(null)}
      recordExitLabel={exitLabel}
      saveState={screen.saveState}
      onRetrySave={screen.retrySave}
      /* La zone 5 est PRETEE au ViewHost et aux vues : c'est lui qui tient les
         etats standard et la memoire de defilement par vue. */
      scrollRef={screen.scrollRef}
      {...(Canvas !== undefined ? propsSurface : {})}
    >
      {Canvas !== undefined ? (
        /* La zone 5 appartient a l'app, et le filet devient explicite :
           `CanvasBoundary` est au canvas ce que le ViewHost est a une vue, a
           ceci pres qu'une surface custom n'a AUCUNE vue de repli. */
        <CanvasBoundary surfaceId={screen.door.id}>
          <Canvas
            fiches={fichesDeCanvas[screen.door.id] ?? []}
            {...cadreSurface.api}
            porte={screen.door.id}
            ficheOuverte={ficheDuCanvas}
            fermerFiche={fermerFicheDuCanvas}
            ouvrirFiche={ouvrirFicheDepuisLeCanvas}
            creerFiche={creerDepuisLeCanvas}
          />
        </CanvasBoundary>
      ) : (
        /* Zone 5 : le ViewHost tient les etats standard dans la meme priorite
           que le gabarit (erreur > chargement > fiche introuvable > vide >
           contenu), la memoire de defilement par vue et le filet anti-plantage.
           L'etat vide suit la porte, et il ne connait pas la creation : la fiche
           vide PREND la zone 5 (niveau `record`), donc elle ne cohabite jamais
           avec l'invitation a ajouter. */
        <PhotosDeFiches.Provider value={photos}>
          <ContexteDeVues.Provider value={contexteDesVues}>
          <ViewHost
            state={screen}
            recordFooter={ficheOuverte !== null && jeuAffiche !== null
              ? <AiHistoryPanel sdk={sdk} family={jeuAffiche.family} recordId={ficheOuverte} />
              : null}
            fichiers={fichiers}
            {...(jeuAffiche === null ? {} : { collection: jeuAffiche.family })}
            errorTitle="Échec du chargement"
            retryLabel="Réessayer"
            {...(vide === undefined ? {} : { emptyTitle: vide.titre })}
            {...(vide?.indication === undefined ? {} : { emptyHint: vide.indication })}
            {...(introuvable === undefined ? {} : { notFoundTitle: introuvable.titre })}
            {...(introuvable?.indication === undefined
              ? {}
              : { notFoundHint: introuvable.indication })}
          />
          </ContexteDeVues.Provider>
        </PhotosDeFiches.Provider>
      )}
    </ScreenLayout>
    {/* LA FENETRE D'UN GESTE DE FICHE, a cote du gabarit comme celles du cockpit :
        un composant de l'app, qui rend son propre `Modal`. */}
    {fenetreDeFiche}
    </>
  );
}

export default EcranDeclare;
